import {
  supportedEncompassRoleById,
  supportedEncompassRoleForLegacySlug
} from '../../../../supabase/functions/_shared/encompass-role-contract';

export type RecipientTarget =
  | { kind: 'user'; user_id: string }
  | { kind: 'user_role'; role: string }
  | { kind: 'raw_email'; email: string }
  | { kind: 'loan_role'; role: 'loan_officer' | 'loan_officer_2' | 'processor' | 'loan_officer_assistant' | 'closer' | 'jr_processor' }
  | { kind: 'encompass_loan_role'; role_id: number; role_name_snapshot: string }
  | { kind: 'loan_party'; party: 'borrower' | 'co_borrower' | 'borrower_pair' }
  | { kind: 'loan_contact'; contact: 'buyer_realtor' | 'escrow_officer' };

export interface RecipientConfigV1 {
  version: 1;
  to: RecipientTarget[];
  cc: RecipientTarget[];
}

export interface TemplateOverrides {
  subject?: string | null;
  body?: string | null;
}

export type RecipientZone = 'to' | 'cc';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && Boolean(value.trim());
}

function isRecipientTarget(value: unknown): value is RecipientTarget {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const target = value as Record<string, unknown>;
  switch (target.kind) {
    case 'user':
      return isNonEmptyString(target.user_id);
    case 'user_role':
      return isNonEmptyString(target.role);
    case 'raw_email':
      return typeof target.email === 'string';
    case 'loan_role':
      return typeof target.role === 'string'
        && [
          'loan_officer',
          'loan_officer_2',
          'processor',
          'loan_officer_assistant',
          'closer',
          'jr_processor'
        ].includes(target.role);
    case 'encompass_loan_role':
      return typeof target.role_id === 'number'
        && Number.isInteger(target.role_id)
        && target.role_id >= 0
        && isNonEmptyString(target.role_name_snapshot);
    case 'loan_party':
      return target.party === 'borrower'
        || target.party === 'co_borrower'
        || target.party === 'borrower_pair';
    case 'loan_contact':
      return target.contact === 'buyer_realtor'
        || target.contact === 'escrow_officer';
    default:
      return false;
  }
}

export function isRecipientConfigV1(value: unknown): value is RecipientConfigV1 {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as { version?: unknown; to?: unknown; cc?: unknown };
  return candidate.version === 1
    && Array.isArray(candidate.to)
    && candidate.to.every(isRecipientTarget)
    && Array.isArray(candidate.cc)
    && candidate.cc.every(isRecipientTarget);
}

export function isSameTarget(a: RecipientTarget, b: RecipientTarget): boolean {
  if (a.kind === 'loan_role' && b.kind === 'encompass_loan_role') {
    return supportedEncompassRoleForLegacySlug(a.role)?.roleId === b.role_id;
  }
  if (a.kind === 'encompass_loan_role' && b.kind === 'loan_role') {
    return supportedEncompassRoleById(a.role_id)?.legacySlug === b.role;
  }
  if (a.kind !== b.kind) {
    return false;
  }
  switch (a.kind) {
    case 'user':
      return b.kind === 'user' && a.user_id === b.user_id;
    case 'user_role':
      return b.kind === 'user_role' && a.role === b.role;
    case 'raw_email':
      return b.kind === 'raw_email' && a.email.trim().toLowerCase() === b.email.trim().toLowerCase();
    case 'loan_role':
      return b.kind === 'loan_role' && a.role === b.role;
    case 'encompass_loan_role':
      return b.kind === 'encompass_loan_role' && a.role_id === b.role_id;
    case 'loan_party':
      return b.kind === 'loan_party' && a.party === b.party;
    case 'loan_contact':
      return b.kind === 'loan_contact' && a.contact === b.contact;
  }
}
